import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Brain, ArrowRight, ArrowLeft, Mail, KeyRound, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      const role = result.data?.role;
      if (role === 'admin') navigate('/admin');
      else if (role === 'employer') navigate('/employer');
      else navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <div className="hidden lg:flex lg:w-1/2 bg-[#111111] items-center justify-center p-16">
        <div>
          <div className="flex items-center gap-3 mb-8">
            <div className="h-14 overflow-hidden flex items-center">
              <img src="/ioa_logo_dark.svg" alt="Institute of Analytics USA" className="h-20 w-auto object-cover" style={{ objectPosition: 'center' }} />
            </div>
          </div>
          <h2 className="text-4xl font-black text-white tracking-tighter leading-tight mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Welcome back.<br />
            <span className="text-[#0077B6]">Continue learning.</span>
          </h2>
          <p className="text-zinc-300 text-sm leading-relaxed max-w-sm">
            Access your courses, track progress, explore job opportunities, and connect with employers.
          </p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <h1 data-testid="login-heading" className="text-3xl font-bold tracking-tight mb-8 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Sign In</h1>
          {error && <div data-testid="login-error" className="mb-6 p-3 bg-[#F59E0B]/15 border border-[#F59E0B]/40 text-[#F59E0B] text-sm">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Email</label>
              <input data-testid="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full px-4 py-3 bg-[#111111] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-sm text-white placeholder-zinc-600" placeholder="your@email.com" />
            </div>
            <div>
              <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Password</label>
              <input data-testid="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required className="w-full px-4 py-3 bg-[#111111] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-sm text-white placeholder-zinc-600" placeholder="Enter your password" />
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit" className="brand-btn w-full text-center py-3 text-sm disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? 'Signing in...' : 'Sign In'} {!loading && <ArrowRight className="w-4 h-4" strokeWidth={1.5} />}
            </button>
          </form>
          <p className="mt-6 text-sm text-zinc-300 text-center">
            Don't have an account? <Link to="/register" className="text-[#0077B6] hover:underline" data-testid="login-to-register">Create one</Link>
          </p>
          <p className="mt-2 text-sm text-zinc-400 text-center">
            <Link to="/forgot-password" className="text-[#F59E0B] hover:underline" data-testid="forgot-password-link">Forgot your password?</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('student');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await register(email, password, name, role);
    setLoading(false);
    if (result.success) {
      if (role === 'employer') navigate('/employer');
      else navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <div className="hidden lg:flex lg:w-1/2 bg-[#111111] items-center justify-center p-16">
        <div>
          <div className="flex items-center gap-3 mb-8">
            <div className="h-14 overflow-hidden flex items-center">
              <img src="/ioa_logo_dark.svg" alt="Institute of Analytics USA" className="h-20 w-auto object-cover" style={{ objectPosition: 'center' }} />
            </div>
          </div>
          <h2 className="text-4xl font-black text-white tracking-tighter leading-tight mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Start your AI career.<br />
            <span className="text-[#0077B6]">Enroll today.</span>
          </h2>
          <p className="text-zinc-300 text-sm leading-relaxed max-w-sm">
            Join the PG Diploma in AI, ML & Data Science Engineering by Institute of Analytics USA&trade;. 6 courses, 120 hours of training, and direct pathways to $80K+ careers.
          </p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <h1 data-testid="register-heading" className="text-3xl font-bold tracking-tight mb-8 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Create Account</h1>
          {error && <div data-testid="register-error" className="mb-6 p-3 bg-[#F59E0B]/15 border border-[#F59E0B]/40 text-[#F59E0B] text-sm">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>I am a</label>
              <div className="flex gap-0 border border-[#262626]">
                <button type="button" data-testid="role-student" onClick={() => setRole('student')} className={`flex-1 py-3 text-sm font-medium transition-colors ${role === 'student' ? 'bg-[#0077B6] text-white' : 'bg-[#111111] text-zinc-200 hover:bg-[#1a1a1a]'}`}>Student</button>
                <button type="button" data-testid="role-employer" onClick={() => setRole('employer')} className={`flex-1 py-3 text-sm font-medium border-l border-[#262626] transition-colors ${role === 'employer' ? 'bg-[#0077B6] text-white' : 'bg-[#111111] text-zinc-200 hover:bg-[#1a1a1a]'}`}>Employer</button>
              </div>
            </div>
            <div>
              <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Full Name</label>
              <input data-testid="register-name" type="text" value={name} onChange={e => setName(e.target.value)} required className="w-full px-4 py-3 bg-[#111111] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-sm text-white placeholder-zinc-600" placeholder="Your full name" />
            </div>
            <div>
              <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Email</label>
              <input data-testid="register-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full px-4 py-3 bg-[#111111] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-sm text-white placeholder-zinc-600" placeholder="your@email.com" />
            </div>
            <div>
              <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Password</label>
              <input data-testid="register-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-sm text-white placeholder-zinc-600" placeholder="Min 6 characters" />
            </div>
            <button type="submit" disabled={loading} data-testid="register-submit" className="brand-btn w-full text-center py-3 text-sm disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? 'Creating account...' : `Create ${role === 'employer' ? 'Employer' : 'Student'} Account`} {!loading && <ArrowRight className="w-4 h-4" strokeWidth={1.5} />}
            </button>
          </form>
          <p className="mt-6 text-sm text-zinc-300 text-center">
            Already have an account? <Link to="/login" className="text-[#0077B6] hover:underline" data-testid="register-to-login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}


export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetUrl, setResetUrl] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email }, { withCredentials: true });
      setSubmitted(true);
      if (data.reset_url) setResetUrl(data.reset_url);
    } catch (e) {
      setError(e.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <div className="hidden lg:flex lg:w-1/2 bg-[#111111] items-center justify-center p-16">
        <div>
          <div className="flex items-center gap-3 mb-8">
            <div className="h-14 overflow-hidden flex items-center">
              <img src="/ioa_logo_dark.svg" alt="Institute of Analytics USA" className="h-20 w-auto object-cover" style={{ objectPosition: 'center' }} />
            </div>
          </div>
          <h2 className="text-4xl font-black text-white tracking-tighter leading-tight mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Reset your password.<br />
            <span className="text-[#0077B6]">Get back on track.</span>
          </h2>
          <p className="text-zinc-300 text-sm leading-relaxed max-w-sm">
            Enter your email and we'll help you reset your password so you can continue your AI learning journey.
          </p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/login" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-[#0077B6] mb-8" data-testid="back-to-login">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to Sign In
          </Link>

          {submitted ? (
            <div data-testid="forgot-password-success">
              <CheckCircle2 className="w-12 h-12 text-green-400 mb-4" strokeWidth={1.5} />
              <h1 className="text-3xl font-bold tracking-tight mb-4 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Check Your Email</h1>
              <p className="text-base text-zinc-300 mb-6">
                If an account exists for <strong className="text-white">{email}</strong>, you'll receive a password reset link shortly.
              </p>
              {resetUrl && (
                <div className="p-4 bg-[#111111] border border-[#262626] mb-6">
                  <p className="text-xs text-[#F59E0B] font-mono font-bold uppercase tracking-wider mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Testing Mode — Reset Link</p>
                  <p className="text-xs text-zinc-400 mb-2">In production, this link will be sent via email. For testing, click below:</p>
                  <Link to={`/reset-password?token=${resetUrl.split('token=')[1]}`} className="text-sm text-[#0077B6] hover:underline break-all" data-testid="reset-link">
                    Click here to reset your password
                  </Link>
                </div>
              )}
              <p className="text-sm text-zinc-400">
                Didn't receive it? <button onClick={() => { setSubmitted(false); setResetUrl(''); }} className="text-[#0077B6] hover:underline">Try again</button>
              </p>
            </div>
          ) : (
            <>
              <Mail className="w-10 h-10 text-[#F59E0B] mb-4" strokeWidth={1.5} />
              <h1 data-testid="forgot-password-heading" className="text-3xl font-bold tracking-tight mb-4 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Forgot Password?</h1>
              <p className="text-base text-zinc-300 mb-8">Enter the email address associated with your account and we'll send you a reset link.</p>
              {error && <div data-testid="forgot-password-error" className="mb-6 p-3 bg-[#F59E0B]/15 border border-[#F59E0B]/40 text-[#F59E0B] text-sm">{error}</div>}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Email</label>
                  <input data-testid="forgot-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full px-4 py-3 bg-[#111111] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-sm text-white placeholder-zinc-600" placeholder="your@email.com" />
                </div>
                <button type="submit" disabled={loading} data-testid="forgot-submit" className="brand-btn w-full text-center py-3 text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? 'Sending...' : 'Send Reset Link'} {!loading && <ArrowRight className="w-4 h-4" strokeWidth={1.5} />}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-zinc-300 mb-4">Invalid reset link.</p>
          <Link to="/forgot-password" className="text-[#0077B6] hover:underline">Request a new one</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <div className="hidden lg:flex lg:w-1/2 bg-[#111111] items-center justify-center p-16">
        <div>
          <div className="flex items-center gap-3 mb-8">
            <div className="h-14 overflow-hidden flex items-center">
              <img src="/ioa_logo_dark.svg" alt="Institute of Analytics USA" className="h-20 w-auto object-cover" style={{ objectPosition: 'center' }} />
            </div>
          </div>
          <h2 className="text-4xl font-black text-white tracking-tighter leading-tight mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Set a new password.<br />
            <span className="text-[#0077B6]">You're almost there.</span>
          </h2>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {success ? (
            <div data-testid="reset-success">
              <CheckCircle2 className="w-12 h-12 text-green-400 mb-4" strokeWidth={1.5} />
              <h1 className="text-3xl font-bold tracking-tight mb-4 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Password Reset!</h1>
              <p className="text-base text-zinc-300 mb-6">Your password has been updated. Redirecting to sign in...</p>
              <Link to="/login" className="text-[#0077B6] hover:underline text-sm">Go to Sign In now</Link>
            </div>
          ) : (
            <>
              <KeyRound className="w-10 h-10 text-[#F59E0B] mb-4" strokeWidth={1.5} />
              <h1 data-testid="reset-password-heading" className="text-3xl font-bold tracking-tight mb-4 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Set New Password</h1>
              <p className="text-base text-zinc-300 mb-8">Choose a strong password for your account.</p>
              {error && <div data-testid="reset-error" className="mb-6 p-3 bg-[#F59E0B]/15 border border-[#F59E0B]/40 text-[#F59E0B] text-sm">{error}</div>}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>New Password</label>
                  <input data-testid="reset-new-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-sm text-white placeholder-zinc-600" placeholder="Min 6 characters" />
                </div>
                <div>
                  <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Confirm Password</label>
                  <input data-testid="reset-confirm-password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required minLength={6} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-sm text-white placeholder-zinc-600" placeholder="Confirm your password" />
                </div>
                <button type="submit" disabled={loading} data-testid="reset-submit" className="brand-btn w-full text-center py-3 text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? 'Resetting...' : 'Reset Password'} {!loading && <ArrowRight className="w-4 h-4" strokeWidth={1.5} />}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
